import { useQuery } from "@tanstack/react-query";

import axiosClient from "./apiClient";

/**
 * Who the signed-in viewer is, and what the API will let them do.
 *
 * Mirrors `cts_api/src/Auth0/Models/DTO/MeDto.cs`, served by
 * `GET api/Auth0/v1/me` (REQ-ACL-8). That endpoint carries no role clause, so any
 * authenticated viewer gets an answer -- a viewer holding no roles is told they can
 * do nothing rather than given a 403 to interpret.
 *
 * Why an endpoint rather than reading the token: this SPA requests only
 * `offline_access openid email` and reads no custom claims, so capabilities would
 * need an Auth0 Action to put them in the ID token -- and that Action would then
 * carry its own copy of the role-to-capability mapping. One source, server-side.
 *
 * There is deliberately NO sensor scope here. The API does not return one yet,
 * because per-user grants exist but nothing enforces them until a tenant opts in,
 * and a field that always said "everything" would be a promise neither side keeps.
 */
export type Capability =
  | "ViewSensors"
  | "ControlSensors"
  | "ProvisionSensors"
  | "ManagePeople"
  | "ManageAccess";

export type ViewerRole = {
  name: string;
  /** "Organization" | "Family" | "Internal" | "Unused" */
  kind: string;
  summary: string;
};

export type Viewer = {
  user_id: string;
  email?: string | null;
  name?: string | null;
  tenant_id?: string | null;
  tenant_name?: string | null;
  /** "family" | "organization" -- decides which invitation path applies. */
  entry_type?: string | null;
  organization_id?: string | null;
  roles: ViewerRole[];
  /** Role names the API has no mapping for. They confer nothing. */
  unmapped_roles: string[];
  /**
   * Whether this tenant enforces per-person sensor access.
   *
   * A property of the tenant, reported on /me because that is the call every
   * client already makes. It changes what the admin screen should SAY: a grant of
   * nothing is a warning about the future in a tenant that does not enforce, and a
   * statement of fact in one that does.
   */
  scope_enforced?: boolean;
  capabilities: Capability[];

  /**
   * Whether THIS token was issued for an organization.
   *
   * The post-login Action resolves the tenant claim from the organization's
   * metadata when a login carries organization context, and from the person's
   * own `app_metadata` otherwise. So this is not cosmetic: false means an
   * organization member is being shown their personal tenant, which for them is
   * empty.
   */
  org_context?: boolean;

  /** Every organization this person may sign in to. Empty for a family user. */
  organizations?: ViewerOrganization[];
};

export type ViewerOrganization = {
  /** Auth0's organization id. What `loginWithRedirect` takes. */
  id: string;
  /** Auth0's slug. Never shown -- nobody knows their employer's slug. */
  name?: string | null;
  /** What to call it on screen. */
  display_name?: string | null;
};

export async function getViewer(): Promise<Viewer> {
  const res = await axiosClient.get<Viewer>("Auth0/v1/me");
  return res.data;
}

export const VIEWER_QUERY_KEY = ["viewer"] as const;

/**
 * The viewer, fetched once.
 *
 * `staleTime: Infinity` because roles and capabilities change when an admin
 * changes them, not while someone is reading a page -- and every re-fetch would
 * re-gate the whole UI mid-interaction. `retry: false` because the useful
 * distinction is "we have an answer" versus "we do not", and three silent retries
 * only delay the second.
 */
export function useViewer() {
  return useQuery({
    queryKey: VIEWER_QUERY_KEY,
    queryFn: getViewer,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Whether the viewer holds a capability.
 *
 * Hiding a control is presentation. The boundary is the server check: a viewer who
 * edits the DOM still gets a 403 from the capability gate and a 404 from the scope
 * guard, per target. This decides what is worth showing, not what is allowed.
 *
 * An absent or still-loading viewer holds nothing, which fails closed.
 */
export function can(
  viewer: Viewer | undefined | null,
  capability: Capability
): boolean {
  return !!viewer?.capabilities?.includes(capability);
}
